import { type Aperture, type ComponentEffect, toRender } from "refract-rxjs";
import { type Observable, debounceTime, filter, map, startWith, take, zip } from "rxjs";

import { type GetLensesListQuery } from "../api/queries";

export type LensesList = GetLensesListQuery["lenses"]["lensesList"];

export const observeNoLensesOnInit = (
    data$: Observable<GetLensesListQuery | undefined>,
    loading$: Observable<boolean>,
): Observable<boolean> =>
    zip([data$, loading$]).pipe(
        filter(([data, loading]) => !loading && data !== undefined),
        map(([data]) => {
            return data?.lenses.lensesList.length === 0;
        }),
        take(2), // takes 2 events - 1 default, one computed
        startWith(false),
    );

export const observeIsFirstFetching = (data$: Observable<GetLensesListQuery | undefined>): Observable<boolean> =>
    data$.pipe(
        filter((data) => data !== undefined),
        map(() => false),
        take(2), // takes 2 events - 1 default, one computed
        startWith(true),
    );

export interface NoLensesOnInitProps {
    data: GetLensesListQuery | undefined;
    loading: boolean;
}

export const noLensesOnInitAperture: Aperture<NoLensesOnInitProps, ComponentEffect<boolean>> = ({ observe }) => {
    const data$ = observe<GetLensesListQuery>("data");
    const loading$ = observe<boolean>("loading");

    return observeNoLensesOnInit(data$, loading$).pipe(map((noLensesOnInit) => toRender<boolean>(noLensesOnInit)));
};

export const isFirstFetchingAperture: Aperture<{ data: GetLensesListQuery | undefined }, ComponentEffect<boolean>> = ({
    observe,
}) => {
    const data$ = observe<GetLensesListQuery>("data");

    return observeIsFirstFetching(data$).pipe(map((isFirstFetching) => toRender<boolean>(isFirstFetching)));
};

export const debouncedSearchAperture: Aperture<{ lensSearchText: string }, ComponentEffect<string>> = ({ observe }) => {
    return observe<string>("lensSearchText").pipe(
        debounceTime(300),
        map((value) => toRender<string>(value)),
    );
};
